import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, ListChecks, Loader2, GripVertical, ShieldCheck, Clock, Save, Camera } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const AREAS = [
  { value: "both", label: "Both" },
  { value: "kitchen", label: "Kitchen" },
  { value: "foh", label: "Front of House" },
];

let _uidSeq = 0;
const uid = () => `u${Date.now().toString(36)}${(_uidSeq++).toString(36)}`;
const withUids = (c) => ({
  ...c,
  sections: c.sections.map((s) => ({ ...s, _uid: uid(), items: s.items.map((it) => ({ ...it, _uid: uid() })) })),
});

export default function ChecklistEditor({ onClose, onSaved }) {
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState(null);
  const [checklists, setChecklists] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/locations");
        const locs = data.locations || [];
        setLocations(locs);
        setLoc(locs[0]?.id || null);
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, []);

  const loadChecklists = async (locId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/checklists?location_id=${locId}`);
      setChecklists(data.checklists || []);
      setActiveId((prev) => (data.checklists?.some((c) => c.id === prev) ? prev : data.checklists?.[0]?.id) || null);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  useEffect(() => { if (loc) loadChecklists(loc); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [loc]);

  useEffect(() => {
    const c = checklists.find((x) => x.id === activeId);
    setDraft(c ? withUids(JSON.parse(JSON.stringify(c))) : null);
  }, [activeId, checklists]);

  const toggleEnabled = async (next) => {
    try {
      await api.put(`/admin/checklists/${activeId}/enabled?location_id=${loc}&enabled=${next}`);
      setChecklists((prev) => prev.map((c) => (c.id === activeId ? { ...c, enabled: next } : c)));
      toast.success(next ? "Checklist turned on for this location" : "Checklist turned off for this location");
      onSaved?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const copyToAll = async () => {
    if (!window.confirm("Copy this checklist (and its on/off state) to ALL other locations, overwriting theirs?")) return;
    try {
      const { data } = await api.post(`/admin/checklists/${activeId}/copy-to-all?location_id=${loc}`);
      toast.success(`Copied to ${data.copied_to} other location(s)`);
      onSaved?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const updateSection = (si, patch) => setDraft((d) => ({
    ...d, sections: d.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)),
  }));

  const updateItem = (si, ii, patch) => setDraft((d) => ({
    ...d,
    sections: d.sections.map((s, i) => (i === si ? { ...s, items: s.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : s)),
  }));

  const addItem = (si) => setDraft((d) => ({
    ...d,
    sections: d.sections.map((s, i) => {
      if (i !== si) return s;
      const blank = s.kind === "timeline"
        ? { _uid: uid(), time: "", title: "", detail: "" }
        : { _uid: uid(), text: "", input: false, expected: null, require_photo: false };
      return { ...s, items: [...s.items, blank] };
    }),
  }));

  const removeItem = (si, ii) => setDraft((d) => ({
    ...d, sections: d.sections.map((s, i) => (i === si ? { ...s, items: s.items.filter((_, j) => j !== ii) } : s)),
  }));

  const addSection = () => setDraft((d) => ({
    ...d,
    sections: [...d.sections, { _uid: uid(), id: null, title: "New Area", area: d.area === "kitchen" ? "kitchen" : d.area === "foh" ? "foh" : "both", kind: "checklist", manager_only: false, require_photo: false, items: [] }],
  }));

  const removeSection = (si) => {
    if (draft.sections.length <= 1) return toast.error("A checklist needs at least one area");
    if (!window.confirm(`Delete the "${draft.sections[si].title}" area and all its tasks?`)) return;
    setDraft((d) => ({ ...d, sections: d.sections.filter((_, i) => i !== si) }));
  };

  const onAreaDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setDraft((d) => {
      const from = d.sections.findIndex((s) => s._uid === active.id);
      const to = d.sections.findIndex((s) => s._uid === over.id);
      if (from < 0 || to < 0) return d;
      return { ...d, sections: arrayMove(d.sections, from, to) };
    });
  };

  const onTaskDragEnd = (si) => (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setDraft((d) => {
      const items = d.sections[si].items;
      const from = items.findIndex((it) => it._uid === active.id);
      const to = items.findIndex((it) => it._uid === over.id);
      if (from < 0 || to < 0) return d;
      return { ...d, sections: d.sections.map((s, i) => (i === si ? { ...s, items: arrayMove(items, from, to) } : s)) };
    });
  };

  const save = async () => {
    if (!draft.title.trim()) return toast.error("Checklist title cannot be empty");
    for (const s of draft.sections) if (!s.title.trim()) return toast.error("Every area needs a title");
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/checklists/${draft.id}?location_id=${loc}`, {
        title: draft.title,
        signoff_roles: draft.signoff_roles || [],
        sections: draft.sections.map((s) => ({
          id: s.id, title: s.title, area: s.area, kind: s.kind,
          manager_only: !!s.manager_only, require_photo: !!s.require_photo,
          items: s.items.map(({ _uid, ...rest }) => rest),
        })),
      });
      setChecklists((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      toast.success("Checklist saved");
      onSaved?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-[#2E2E2E] bg-[#171717] max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-[#2E2E2E]">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><ListChecks className="h-5 w-5 text-[#E11D2A]" /> Edit Checklists</h3>
          <div className="flex items-center gap-2">
            {locations.length > 0 && (
              <select data-testid="editor-location-select" value={loc || ""} onChange={(e) => setLoc(e.target.value)}
                className="h-9 rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-2.5 text-sm outline-none focus:border-[#E11D2A] max-w-[160px]">
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            <button onClick={onClose} data-testid="checklist-editor-close" className="h-8 w-8 rounded-lg hover:bg-[#232323] flex items-center justify-center"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#E11D2A]" /></div>
        ) : (
          <>
            <div className="shrink-0 px-4 pt-4 flex flex-wrap gap-2">
              {checklists.map((c) => (
                <button key={c.id} data-testid={`editor-checklist-tab-${c.id}`} onClick={() => setActiveId(c.id)}
                  className={`rounded-lg px-3.5 py-2 text-sm font-semibold border transition-colors ${activeId === c.id ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "bg-[#0E0E0E] border-[#2E2E2E] text-[#A3A3A3] hover:border-[#E11D2A]/50"}`}>
                  {c.title}
                </button>
              ))}
            </div>

            {draft && (
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1.5">Checklist name</label>
                  <input data-testid="editor-checklist-title" value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                    className="w-full rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A]" />
                </div>
                {(() => { const cur = checklists.find((c) => c.id === activeId); const on = cur ? cur.enabled : true; return (
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-[#2E2E2E] bg-[#0E0E0E] px-4 py-3">
                    <button type="button" data-testid="editor-enabled-toggle" onClick={() => toggleEnabled(!on)} className="flex items-center gap-2.5 text-left">
                      <span className={`shrink-0 h-6 w-11 rounded-full p-0.5 transition-colors ${on ? "bg-[#16A34A]" : "bg-[#3a3a3a]"}`}><span className={`block h-5 w-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} /></span>
                      <span className="text-sm">{on ? "On for this location" : "Off — hidden at this location"}</span>
                    </button>
                    <button type="button" data-testid="editor-copy-all" onClick={copyToAll} className="text-xs font-semibold text-[#A3A3A3] hover:text-[#E11D2A]">Copy to all locations</button>
                  </div>
                ); })()}
                <p className="text-[11px] text-[#6B6B6B] flex items-center gap-1.5"><GripVertical className="h-3.5 w-3.5" /> Drag the handles to reorder areas and tasks — this is the order the crew sees.</p>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onAreaDragEnd}>
                  <SortableContext items={draft.sections.map((s) => s._uid)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-4">
                      {draft.sections.map((s, si) => (
                        <SortableArea key={s._uid} section={s} si={si}
                          updateSection={updateSection} removeSection={removeSection}
                          updateItem={updateItem} addItem={addItem} removeItem={removeItem}
                          onTaskDragEnd={onTaskDragEnd(si)} sensors={sensors} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                <button data-testid="editor-add-section" onClick={addSection} className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#2E2E2E] py-3 text-sm font-medium text-[#A3A3A3] hover:border-[#E11D2A]/60 hover:text-[#F5F5F5] transition-colors">
                  <Plus className="h-4 w-4" /> Add area
                </button>
              </div>
            )}

            <div className="shrink-0 px-5 py-4 border-t border-[#2E2E2E]">
              <button data-testid="editor-save-button" onClick={save} disabled={saving || !draft} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#E11D2A] text-white font-heading font-bold py-3 hover:bg-[#B91C1C] disabled:opacity-60 transition-colors">
                <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SortableArea({ section: s, si, updateSection, removeSection, updateItem, addItem, removeItem, onTaskDragEnd, sensors }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s._uid });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`editor-section-${si}`} className="rounded-xl border border-[#2E2E2E] bg-[#0E0E0E] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <button {...attributes} {...listeners} data-testid={`editor-drag-section-${si}`} className="shrink-0 h-8 w-6 flex items-center justify-center text-[#4a4a4a] hover:text-[#E11D2A] cursor-grab active:cursor-grabbing touch-none" title="Drag to reorder area"><GripVertical className="h-4 w-4" /></button>
        <input data-testid={`editor-section-title-${si}`} value={s.title} onChange={(e) => updateSection(si, { title: e.target.value })}
          className="flex-1 rounded-lg bg-[#171717] border border-[#2E2E2E] px-3 py-2 text-sm font-semibold outline-none focus:border-[#E11D2A]" />
        <select value={s.area} onChange={(e) => updateSection(si, { area: e.target.value })}
          className="rounded-lg bg-[#171717] border border-[#2E2E2E] px-2 py-2 text-xs outline-none focus:border-[#E11D2A]">
          {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <button data-testid={`editor-delete-section-${si}`} onClick={() => removeSection(si)} className="h-8 w-8 shrink-0 rounded-lg hover:bg-[#E11D2A]/15 flex items-center justify-center text-[#A3A3A3] hover:text-[#E11D2A] transition-colors"><Trash2 className="h-4 w-4" /></button>
      </div>

      <div className="flex items-center gap-4 pl-8 text-[11px] text-[#6B6B6B]">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={!!s.manager_only} onChange={(e) => updateSection(si, { manager_only: e.target.checked })} className="accent-[#E11D2A]" />
          <ShieldCheck className="h-3 w-3" /> Manager only
        </label>
        {s.kind === "timeline" && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Schedule</span>}
      </div>

      <div className="pl-8">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onTaskDragEnd}>
          <SortableContext items={s.items.map((it) => it._uid)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {s.items.map((it, ii) => (
                <SortableTask key={it._uid} item={it} si={si} ii={ii} kind={s.kind} updateItem={updateItem} removeItem={removeItem} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <button data-testid={`editor-add-item-${si}`} onClick={() => addItem(si)} className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-[#2E2E2E] px-3 py-1.5 text-xs font-medium text-[#A3A3A3] hover:border-[#E11D2A]/60 hover:text-[#F5F5F5] transition-colors">
          <Plus className="h-3.5 w-3.5" /> Add task
        </button>
      </div>
    </div>
  );
}

function SortableTask({ item: it, si, ii, kind, updateItem, removeItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: it._uid });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-1.5">
      <button {...attributes} {...listeners} data-testid={`editor-drag-item-${si}-${ii}`} className="shrink-0 h-9 w-5 flex items-center justify-center text-[#3a3a3a] hover:text-[#E11D2A] cursor-grab active:cursor-grabbing touch-none" title="Drag to reorder task"><GripVertical className="h-3.5 w-3.5" /></button>
      {kind === "timeline" ? (
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-1.5">
          <input value={it.time || ""} onChange={(e) => updateItem(si, ii, { time: e.target.value })} placeholder="Time" className="rounded-lg bg-[#171717] border border-[#2E2E2E] px-2.5 py-2 text-sm outline-none focus:border-[#E11D2A]" />
          <input value={it.title || ""} onChange={(e) => updateItem(si, ii, { title: e.target.value })} placeholder="Title" className="rounded-lg bg-[#171717] border border-[#2E2E2E] px-2.5 py-2 text-sm outline-none focus:border-[#E11D2A]" />
          <input value={it.detail || ""} onChange={(e) => updateItem(si, ii, { detail: e.target.value })} placeholder="Detail" className="sm:col-span-2 rounded-lg bg-[#171717] border border-[#2E2E2E] px-2.5 py-2 text-sm outline-none focus:border-[#E11D2A]" />
        </div>
      ) : (
        <>
          <input data-testid={`editor-item-${si}-${ii}`} value={it.text || ""} onChange={(e) => updateItem(si, ii, { text: e.target.value })} placeholder="Task description"
            className="flex-1 rounded-lg bg-[#171717] border border-[#2E2E2E] px-3 py-2 text-sm outline-none focus:border-[#E11D2A]" />
          <button type="button" data-testid={`editor-photo-toggle-${si}-${ii}`} onClick={() => updateItem(si, ii, { require_photo: !it.require_photo })}
            title={it.require_photo ? "Photo required — click to make optional" : "Require a photo for this task"}
            className={`h-9 w-9 shrink-0 rounded-lg border flex items-center justify-center transition-colors ${it.require_photo ? "border-[#E11D2A] bg-[#E11D2A]/15 text-[#E11D2A]" : "border-[#2E2E2E] bg-[#171717] text-[#4a4a4a] hover:text-[#A3A3A3] hover:border-[#E11D2A]/40"}`}>
            <Camera className="h-3.5 w-3.5" />
          </button>
        </>
      )}
      <button data-testid={`editor-delete-item-${si}-${ii}`} onClick={() => removeItem(si, ii)} className="h-9 w-9 shrink-0 rounded-lg hover:bg-[#E11D2A]/15 flex items-center justify-center text-[#6B6B6B] hover:text-[#E11D2A] transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}
